import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import useEventCallback from '@ecp/common/src/hooks/useEventCallback';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { TextBox } from '@ecp/common/src/text/TextBox';
import Textarea from '@ecp/common/src/components/input/bo/Textarea';
import RadioGroup from '@ecp/common/src/components/radio/bo/RadioGroup';
import Popup from '@ecp/common/src/components/modal/bo/Popup';
import { StyledErrorMessage } from '@ecp/common/src/components/input/Input';

import useValidate from '@bo-hooks/common/useValidate';
import { useConfirmDialog } from '@bo-hooks/common/useModal';
import siteApi from '@bo-apis/system/siteApi';
import ImageFileUploader from '@bo-components/common/ImageFileUploader';
import InfoTextBox from '@bo-components/promotion/InfoTextBox';
import FormField from '@bo-components/common/form/FormField';
import Form from '@bo-components/common/form/Form';
import { UNSAVED_CONFIRM_MESSAGE } from '@bo-pages/system/consts/SystemConsts';
import { useSiteImage } from '../hooks/SiteImagePopupHooks';
import {
  bannerImageItemList,
  formValidators,
  initialError,
  initialForm,
  bulkLogoImageItemList,
  loginLogoImageItemList,
  pcImageItemList,
  typeOptions,
  ficoImageItemList,
} from '../consts/SiteImagePcPopupConst';
import { FILE_EXTENSION } from '@ecp/common/src/components/unstyled/input/FileUploaderUnstyled';

const SiteImagePcPopup = ({ open, onClose, siteInfo, welmallInfo, refreshSiteInfo }) => {
  const [form, setForm] = useState({ ...initialForm });
  const [error, setError] = useState({ ...initialError });
  const { formValidate } = useValidate();
  const { showConfirmDialog } = useConfirmDialog();
  const [hasChangedData, setHasChangedData] = useState(false);

  const { setRequestImageInfo, setFormFileInfo } = useSiteImage({ siteInfo, setForm, form, welmallInfo });

  useEffect(() => {
    if (open) {
      setForm({
        pcLogoBasicUseYn: siteInfo.pcLogoBasicUseYn,
        pcLoginBasicUseYn: siteInfo.pcLoginBasicUseYn,
        pcBulkpBasicUseYn: siteInfo.pcBulkpBasicUseYn,
        pcPromoBasicUseYn: siteInfo.pcPromoBasicUseYn,
        pcFicoBasicUseYn: siteInfo.pcFicoBasicUseYn,
        loginPhrase: siteInfo.pcLoginPhrase || '',
        bulkPhrase: siteInfo.pcBulkpPhrase || '',
      });
      setFormFileInfo(
        siteInfo.pcLogoBasicUseYn,
        'pcImageFileList',
        [
          { key: 'pcGnbImgFile', type: 'pcGnbImgFileId' },
          { key: 'pcFooterImgFile', type: 'pcFooterImgFileId' },
        ],
        'pcLogoBasicUseYn'
      );
      setFormFileInfo(
        siteInfo.pcLoginBasicUseYn,
        'loginImageFileList',
        [{ key: 'pcLoginImgFile', type: 'pcLoginImgFileId' }],
        'pcLoginBasicUseYn'
      );
      setFormFileInfo(
        siteInfo.pcBulkpBasicUseYn,
        'bulkImageFileList',
        [{ key: 'pcBulkpImgFile', type: 'pcBulkpImgFileId' }],
        'pcBulkpBasicUseYn'
      );
      setFormFileInfo(
        siteInfo.pcPromoBasicUseYn,
        'promoImageFileList',
        [{ key: 'pcPromoBannerImgFile', type: 'pcPromoBannerImgFileId' }],
        'pcPromoBasicUseYn'
      );
      setFormFileInfo(
        siteInfo.pcFicoBasicUseYn,
        'ficoImageFileList',
        [{ key: 'pcFicoImgFile', type: 'pcFicoImgFileId' }],
        'pcFicoBasicUseYn'
      );
      setError({ ...initialError });
      setHasChangedData(false);
    }
  }, [open, siteInfo, welmallInfo, setFormFileInfo]);

  const handleFormChange = useEventCallback((v, data) => {
    setHasChangedData(true);
    setForm((prev) => ({ ...prev, [data.key]: v }));
  });

  const handleCustFormChange = useCallback((val) => {
    setHasChangedData(true);
    setForm((prev) => ({
      ...prev,
      ...val,
    }));
  }, []);

  const handleConfirm = async () => {
    const validated = await formValidate('always', form, setError, formValidators);
    if (!validated) return;

    const requestData = {
      siteNumber: siteInfo.siteNumber,
      pcBulkpPhrase: form.bulkPhrase,
      pcLoginPhrase: form.loginPhrase,
      pcLogoBasicUseYn: form.pcLogoBasicUseYn,
      pcLoginBasicUseYn: form.pcLoginBasicUseYn,
      pcBulkpBasicUseYn: form.pcBulkpBasicUseYn,
      pcPromoBasicUseYn: form.pcPromoBasicUseYn,
      pcFicoBasicUseYn: form.pcFicoBasicUseYn,
    };

    showConfirmDialog('저장하시겠습니까?', () => updateSiteImage(requestData));
  };

  const updateSiteImage = async (requestData) => {
    await setRequestImageInfo({
      requestData,
      useYnKey: 'pcLogoBasicUseYn',
      fileIdKeyList: ['pcGnbImgFileId', 'pcFooterImgFileId'],
      fileListKey: 'pcImageFileList',
    });
    await setRequestImageInfo({
      requestData,
      useYnKey: 'pcLoginBasicUseYn',
      fileIdKeyList: ['pcLoginImgFileId'],
      fileListKey: 'loginImageFileList',
    });
    await setRequestImageInfo({
      requestData,
      useYnKey: 'pcBulkpBasicUseYn',
      fileIdKeyList: ['pcBulkpImgFileId'],
      fileListKey: 'bulkImageFileList',
    });
    await setRequestImageInfo({
      requestData,
      useYnKey: 'pcPromoBasicUseYn',
      fileIdKeyList: ['pcPromoBannerImgFileId'],
      fileListKey: 'promoImageFileList',
    });
    await setRequestImageInfo({
      requestData,
      useYnKey: 'pcFicoBasicUseYn',
      fileIdKeyList: ['pcFicoImgFileId'],
      fileListKey: 'ficoImageFileList',
    });

    await siteApi.updateSitePcLogoImage(requestData);

    refreshSiteInfo();
    onClose();
  };

  const handlePcTypeChange = ({ target: { value } }) => {
    setHasChangedData(true);
    setFormFileInfo(
      value,
      'pcImageFileList',
      [
        { key: 'pcGnbImgFile', type: 'pcGnbImgFileId' },
        { key: 'pcFooterImgFile', type: 'pcFooterImgFileId' },
      ],
      'pcLogoBasicUseYn'
    );
    handleCustFormChange({ pcLogoBasicUseYn: value });
  };

  const handleLoginTypeChange = ({ target: { value } }) => {
    setHasChangedData(true);
    setFormFileInfo(
      value,
      'loginImageFileList',
      [{ key: 'pcLoginImgFile', type: 'pcLoginImgFileId' }],
      'pcLoginBasicUseYn'
    );
    setForm((prev) => ({
      ...prev,
      loginPhrase: value === 'Y' ? welmallInfo.pcLoginPhrase || '' : '',
    }));
    handleCustFormChange({ pcLoginBasicUseYn: value });
  };

  const handleBulkTypeChange = ({ target: { value } }) => {
    setHasChangedData(true);
    setFormFileInfo(
      value,
      'bulkImageFileList',
      [{ key: 'pcBulkpImgFile', type: 'pcBulkpImgFileId' }],
      'pcBulkpBasicUseYn'
    );
    setForm((prev) => ({
      ...prev,
      bulkPhrase: value === 'Y' ? welmallInfo.pcBulkpPhrase || '' : '',
    }));
    handleCustFormChange({ pcBulkpBasicUseYn: value });
  };

  const handleBannerTypeChange = ({ target: { value } }) => {
    setHasChangedData(true);
    setFormFileInfo(
      value,
      'promoImageFileList',
      [{ key: 'pcPromoBannerImgFile', type: 'pcPromoBannerImgFileId' }],
      'pcPromoBasicUseYn'
    );
    handleCustFormChange({ pcPromoBasicUseYn: value });
  };

  const handleFicoTypeChange = ({ target: { value } }) => {
    setHasChangedData(true);
    setFormFileInfo(
      value,
      'ficoImageFileList',
      [{ key: 'pcFicoImgFile', type: 'pcFicoImgFileId' }],
      'pcFicoBasicUseYn'
    );
    handleCustFormChange({ pcFicoBasicUseYn: value });
  };

  const closePopup = () => {
    if (hasChangedData) {
      showConfirmDialog(UNSAVED_CONFIRM_MESSAGE, onClose);
    } else {
      onClose();
    }
  };

  return (
    <Popup
      type={'Medium'}
      open={open}
      rootStyleProps={{ width: '700px' }}
      header={'로고및프로모션배너수정-PC'}
      onConfirmClick={handleConfirm}
      onClose={closePopup}
      onCancelClick={closePopup}
      confirmButtonText={'저장'}
    >
      <FlexBoxColumn width={'100%'} align-items={'flex-start'}>
        {welmallInfo.siteNumber === siteInfo.siteNumber && (
          <>
            <InfoTextBox>{`웰스토리몰의 내용 등록/변경은 '직접 입력'을 통해서만 가능합니다.`}</InfoTextBox>
            <Spacing bottom={'8px'} />
          </>
        )}
        <Form>
          <FormField label={'구분'} full>
            <RadioGroup value={form.pcLogoBasicUseYn} itemList={typeOptions} onChange={handlePcTypeChange} />
          </FormField>
          <FormField label={'LOGO'} required full>
            <FlexBoxColumn align-items={'flex-start'}>
              <ImageFileUploader
                readOnly={form.pcLogoBasicUseYn === 'Y'}
                imageFileList={form.pcImageFileList}
                setImageFileList={(pcImageFileList) => handleCustFormChange({ pcImageFileList })}
                imageItemList={pcImageItemList(form.pcLogoBasicUseYn)}
                guideMessages={
                  form.pcLogoBasicUseYn === 'Y'
                    ? []
                    : ['등록 이미지는 개당 최대 20MB를 넘을 수 없습니다.\n(JPG, JPEG, GIF, PNG, SVG 확장자 등록가능)']
                }
              />
              {error.pcImageFileList && <StyledErrorMessage>{error.pcImageFileList}</StyledErrorMessage>}
            </FlexBoxColumn>
          </FormField>
          <FormField label={'구분'} full>
            <RadioGroup value={form.pcLoginBasicUseYn} itemList={typeOptions} onChange={handleLoginTypeChange} />
          </FormField>
          <FormField label={'로그인'} required full>
            <FlexBoxColumn align-items={'flex-start'}>
              <FlexBox>
                <ImageFileUploader
                  readOnly={form.pcLoginBasicUseYn === 'Y'}
                  imageFileList={form.loginImageFileList}
                  setImageFileList={(loginImageFileList) => handleCustFormChange({ loginImageFileList })}
                  imageItemList={loginLogoImageItemList(form.pcLoginBasicUseYn)}
                />
                <Spacing right={'14px'} />
                <FlexBoxColumn align-items={'flex-start'}>
                  <TextBox color={'#666666'}>문구</TextBox>
                  <Textarea
                    value={form.loginPhrase}
                    data-key={'loginPhrase'}
                    onChange={handleFormChange}
                    width={'220px'}
                    maxLength={1000}
                    disabled={form.pcLoginBasicUseYn === 'Y'}
                  />
                </FlexBoxColumn>
              </FlexBox>
              {form.pcLoginBasicUseYn === 'N' && (
                <InfoTextBox>
                  {'등록 이미지는 개당 최대 20MB를 넘을 수 없습니다.\n(JPG, JPEG, GIF, PNG, SVG 확장자 등록가능)'}
                </InfoTextBox>
              )}

              {(error.loginImageFileList || error.loginPhrase) && (
                <StyledErrorMessage>{error.loginImageFileList || error.loginPhrase}</StyledErrorMessage>
              )}
            </FlexBoxColumn>
          </FormField>
          <FormField label={'구분'} full>
            <RadioGroup
              data-key={'pcBulkpBasicUseYn'}
              value={form.pcBulkpBasicUseYn}
              itemList={typeOptions}
              onChange={handleBulkTypeChange}
            />
          </FormField>
          <FormField label={'대량구매'} required full>
            <FlexBoxColumn align-items={'flex-start'}>
              <FlexBox>
                <ImageFileUploader
                  readOnly={form.pcBulkpBasicUseYn === 'Y'}
                  imageFileList={form.bulkImageFileList}
                  setImageFileList={(bulkImageFileList) => handleCustFormChange({ bulkImageFileList })}
                  imageItemList={bulkLogoImageItemList(form.pcBulkpBasicUseYn)}
                />
                <Spacing right={'14px'} />
                <FlexBoxColumn align-items={'flex-start'}>
                  <TextBox color={'#666666'}>문구</TextBox>
                  <Textarea
                    value={form.bulkPhrase}
                    data-key={'bulkPhrase'}
                    onChange={handleFormChange}
                    width={'220px'}
                    maxLength={1000}
                    disabled={form.pcBulkpBasicUseYn === 'Y'}
                  />
                </FlexBoxColumn>
              </FlexBox>
              {form.pcBulkpBasicUseYn === 'N' && (
                <InfoTextBox>
                  {'등록 이미지는 개당 최대 20MB를 넘을 수 없습니다.\n(JPG, JPEG, GIF, PNG, SVG 확장자 등록가능)'}
                </InfoTextBox>
              )}
              {(error.bulkImageFileList || error.bulkPhrase) && (
                <StyledErrorMessage>{error.bulkImageFileList || error.bulkPhrase}</StyledErrorMessage>
              )}
            </FlexBoxColumn>
          </FormField>
          <FormField label={'구분'} full>
            <RadioGroup
              data-key={'pcPromoBasicUseYn'}
              value={form.pcPromoBasicUseYn}
              itemList={typeOptions}
              onChange={handleBannerTypeChange}
            />
          </FormField>
          <FormField label={'프로모션배너'} required full>
            <FlexBoxColumn align-items={'flex-start'}>
              <ImageFileUploader
                readOnly={form.pcPromoBasicUseYn === 'Y'}
                imageFileList={form.promoImageFileList}
                setImageFileList={(promoImageFileList) => handleCustFormChange({ promoImageFileList })}
                imageItemList={bannerImageItemList(form.pcPromoBasicUseYn)}
                guideMessages={
                  form.pcPromoBasicUseYn === 'Y'
                    ? []
                    : ['등록 이미지는 개당 최대 20MB를 넘을 수 없습니다.\n(JPG, JPEG, GIF, PNG, SVG 확장자 등록가능)']
                }
              />
              {error.promoImageFileList && <StyledErrorMessage>{error.promoImageFileList}</StyledErrorMessage>}
            </FlexBoxColumn>
          </FormField>
          <FormField label={'구분'} full>
            <RadioGroup
              data-key={'pcFicoBasicUseYn'}
              value={form.pcFicoBasicUseYn}
              itemList={typeOptions}
              onChange={handleFicoTypeChange}
            />
          </FormField>
          <FormField label={'파비콘'} required full>
            <FlexBoxColumn align-items={'flex-start'}>
              <ImageFileUploader
                extension={FILE_EXTENSION.ICO}
                readOnly={form.pcFicoBasicUseYn === 'Y'}
                imageFileList={form.ficoImageFileList}
                setImageFileList={(ficoImageFileList) => handleCustFormChange({ ficoImageFileList })}
                imageItemList={ficoImageItemList(form.pcFicoBasicUseYn)}
                cardProps={{ width: '30px', height: '30px' }}
                width={'90px'}
                height={'90px'}
                text={''}
                guideMessages={
                  form.pcFicoBasicUseYn === 'Y'
                    ? []
                    : ['ICO 형식의 파일을 업로드해 주세요.']
                }
              />
              {error.ficoImageFileList && <StyledErrorMessage>{error.ficoImageFileList}</StyledErrorMessage>}
            </FlexBoxColumn>
          </FormField>
        </Form>
        <Spacing bottom={'12px'} />
      </FlexBoxColumn>
    </Popup>
  );
};

export default SiteImagePcPopup;

SiteImagePcPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  refreshSiteInfo: PropTypes.func,
  siteInfo: PropTypes.object,
  welmallInfo: PropTypes.object,
};
